'use client'
import dynamic from 'next/dynamic'
import { useEffect, useMemo, useState } from 'react'
import type { Settings } from 'react-slick'
import { PrismicNextImage } from '@prismicio/next'
import { PrismicRichText } from '@prismicio/react'
import { sliderSettingsWithDot } from '@/utils/sliderSettings'
import '@/styles/vendors/slickCarousel'

// Lazy load Slider to reduce initial bundle size
const Slider = dynamic<Settings>(() => import('react-slick'), {
  ssr: false,
  loading: () => <div className="h-64 animate-pulse rounded-md bg-gray-100" aria-hidden="true" />,
})

type ItemObject = Record<string, any>
type DataObject = Record<string, any>

type FeaturesComponentProps = {
  data: DataObject
  type?: string
}

/** CSS display widths for feature icons (`max-w-sm` / `max-w-[250px]`). */
const FEATURED_DISPLAY_WIDTH = 384
const DEFAULT_DISPLAY_WIDTH = 250
const DEFAULT_ASPECT = 1196 / 753

/**
 * Responsive Imgix sizing for decorative feature icons.
 * Caps download near display size (2× for retina) instead of full CMS dimensions.
 */
function getFeatureIconImageProps(item: ItemObject) {
  const isFeatured = Boolean(item.isfeatured)
  const displayWidth = isFeatured ? FEATURED_DISPLAY_WIDTH : DEFAULT_DISPLAY_WIDTH
  const dims = item.icon?.dimensions as { width?: number; height?: number } | undefined
  const aspect =
    dims?.width && dims?.height && dims.height > 0 ? dims.width / dims.height : DEFAULT_ASPECT
  const height = Math.round(displayWidth / aspect)
  const iconUrl = typeof item.icon?.url === 'string' ? item.icon.url : ''
  const isSvg = /\.svg(\?|$)/i.test(iconUrl)

  return {
    width: displayWidth,
    height,
    // Request up to 2× display width for rasters; SVGs ignore usefully and must stay unoptimized
    imgixParams: isSvg ? undefined : { w: displayWidth * 2, q: 75 },
    unoptimized: isSvg,
    sizes: isFeatured ? '(max-width: 1023px) 250px, 384px' : '250px',
  }
}

const FeaturesComponent = ({ data, type = 'offer' }: FeaturesComponentProps) => {
  const getGridColSpan = (item: ItemObject): string => (item.isfeatured ? 'col-span-4' : 'col-span-2')

  const getImagePosition = (index: number): string => {
    const positions = {
      0: '-bottom-14 -right-16 xl:bottom-0 xl:right-0',
      1: '-bottom-24 -right-24 xl:-bottom-10 xl:-right-14',
      2: '-bottom-24 -right-24 xl:-bottom-6 xl:-right-12',
      3: '-bottom-24 -right-24 xl:-bottom-8 xl:-right-14',
      4: '-bottom-24 -right-24 xl:-bottom-6 xl:-right-12',
    }
    return positions[index as keyof typeof positions] || ''
  }

  const getSectionPadding = (type: string | undefined): string => {
    // Company Features is a standalone ink band (unlike Homepage, which continues
    // from Clients). MAR-5696 AC asks for 96px; Homepage desktop bottom is lg:pb-[72px].
    if (type === 'companySmallFeatures') {
      return 'py-16 lg:py-[96px]'
    }
    return `pb-16 ${type ? 'lg:pb-8' : 'lg:pb-[72px]'}`
  }

  const getTypeStyles = (type: string | undefined): string => {
    if (type === 'offer') {
      return 'pt-16 lg:pt-8'
    }
    if (type === 'ai') {
      return 'mb-16 py-16 lg:py-[80px]'
    }
    return ''
  }

  // WCAG: pause autoplay when user prefers reduced motion
  const [reduceMotion, setReduceMotion] = useState(false)
  useEffect(() => {
    const mq = window.matchMedia('(prefers-reduced-motion: reduce)')
    const sync = () => setReduceMotion(mq.matches)
    sync()
    mq.addEventListener('change', sync)
    return () => mq.removeEventListener('change', sync)
  }, [])

  const mobileSliderSettings = useMemo<Settings>(
    () => ({
      ...sliderSettingsWithDot,
      autoplay: !reduceMotion,
    }),
    [reduceMotion],
  )

  return (
    <section
      className={`bg-primary-ink-100 ${getSectionPadding(type)} ${getTypeStyles(type)}`}
      aria-label="Features">
      <div className="container-fluid">
        {/* Desktop version */}
        <div className="hidden lg:grid lg:grid-cols-6 lg:gap-8">
          {data.items.map((item: ItemObject, index: number) => {
            const imageProps = getFeatureIconImageProps(item)
            return (
              <div
                key={item.tag ? `${item.tag}-${index}` : `feature-${index}`}
                className={`${getGridColSpan(item)} relative z-10 min-h-[500px] overflow-hidden rounded-md border border-primary-white-10 bg-primary-white-10 px-8 py-6 [&>ul>li]:relative [&>ul>li]:mb-2 [&>ul>li]:ml-6 [&>ul>li]:font-halyardDisplay [&>ul>li]:text-base [&>ul>li]:font-normal [&>ul>li]:leading-[140%] [&>ul>li]:tracking-[0.16px] [&>ul>li]:!text-white [&>ul>li]:before:absolute [&>ul>li]:before:left-[-25px] [&>ul>li]:before:top-[-2px] [&>ul>li]:before:h-5 [&>ul>li]:before:w-5 [&>ul>li]:before:bg-[url(/images/check.svg)] [&>ul>li]:before:content-['']`}>
                {item.tag && <p className="uppercase text-primary-green-85">{item.tag}</p>}

                <PrismicRichText
                  field={item.title}
                  components={{
                    heading3: ({ children }) => (
                      <h3
                        className={`${item.isfeatured ? 'xl:max-w-3xl' : 'xl:max-w-[310px]'} mb-2 mt-4 font-normal !leading-[110%] -tracking-[0.4px] text-white`}>
                        {children}
                      </h3>
                    ),
                  }}
                />

                <PrismicRichText
                  field={item.description}
                  components={{
                    paragraph: ({ children }) => (
                      <p
                        className={`${item.isfeatured ? 'mb-6 xl:max-w-3xl' : 'mb-5 xl:max-w-[310px]'} text-[22px] font-normal !leading-[140%] tracking-[0.22px] text-white/60`}>
                        {children}
                      </p>
                    ),
                  }}
                />

                {/* Decorative background art — text already describes the feature */}
                <PrismicNextImage
                  field={item.icon}
                  width={imageProps.width}
                  height={imageProps.height}
                  sizes={imageProps.sizes}
                  imgixParams={imageProps.imgixParams}
                  unoptimized={imageProps.unoptimized}
                  alt=""
                  loading="lazy"
                  decoding="async"
                  aria-hidden="true"
                  className={`pointer-events-none absolute -z-[1] h-auto ${item.isfeatured ? 'max-w-sm' : 'max-w-[250px]'} ${getImagePosition(index)}`}
                />
              </div>
            )
          })}
        </div>

        {/* Mobile version */}
        <div className="block lg:hidden [&_.slick-slider]:-mx-4 [&_.slick-slider]:my-0 [&_.slick-slider_.slick-active_button::before]:text-primary-green-85 [&_.slick-slider_.slick-dots]:-bottom-11 [&_.slick-slider_.slick-dots_li]:m-0 [&_.slick-slider_.slick-slide]:px-3 [&_.slick-slider_button::before]:text-xs [&_.slick-slider_button::before]:text-primary-ink-30 [&_.slick-slider_button::before]:opacity-100">
          <Slider {...mobileSliderSettings}>
            {data.items.map((item: ItemObject, index: number) => {
              const imageProps = getFeatureIconImageProps({ ...item, isfeatured: false })
              return (
                <div
                  key={item.tag ? `${item.tag}-mobile-${index}` : `feature-mobile-${index}`}
                  className="relative min-h-[600px] overflow-hidden rounded-md border-2 border-primary-white-10 bg-primary-white-10 px-8 py-6 [&>ul>li]:relative [&>ul>li]:mb-2 [&>ul>li]:ml-6 [&>ul>li]:text-sm [&>ul>li]:text-white [&>ul>li]:before:absolute [&>ul>li]:before:left-[-25px] [&>ul>li]:before:top-[-2px] [&>ul>li]:before:h-5 [&>ul>li]:before:w-5 [&>ul>li]:before:bg-[url(/images/check.svg)] [&>ul>li]:before:content-['']">
                  {item.tag && <p className="uppercase text-primary-green-85">{item.tag}</p>}

                  <PrismicRichText
                    field={item.title}
                    components={{
                      heading3: ({ children }) => (
                        <h3 className={`${item.isfeatured ? 'my-5' : 'mb-5'} text-white`}>{children}</h3>
                      ),
                    }}
                  />

                  <PrismicRichText
                    field={item.description}
                    components={{
                      paragraph: ({ children }) => <p className="text-sm-normal mb-5 mt-2 text-white/60">{children}</p>,
                    }}
                  />

                  <PrismicNextImage
                    field={item.icon}
                    width={imageProps.width}
                    height={imageProps.height}
                    sizes={imageProps.sizes}
                    imgixParams={imageProps.imgixParams}
                    unoptimized={imageProps.unoptimized}
                    alt=""
                    loading="lazy"
                    decoding="async"
                    aria-hidden="true"
                    className="pointer-events-none absolute -bottom-16 -right-4 h-auto max-w-[250px]"
                  />
                </div>
              )
            })}
          </Slider>
        </div>
      </div>
    </section>
  )
}

export default FeaturesComponent
